import i18n from 'i18next';
import { censurarTexto, chavePix, data, dataHora, dataIsoValida, dinheiro, documentoFormatado, email, endereco, mesAno, mesIsoValido, numero, numeroConta, telefoneFormatado, type EnderecoTexto } from './formato';
import { discretoAtivo, OCULTO } from './discreto';
import { ehDocumentoPorNome, ehOcultoPorNome, sensivelPorNome } from './sensivel-por-nome';

/**
 * M6, M7: nomes legíveis para o que o servidor devolve cru (campos de histórico e auditoria, valores,
 * ações, entidades, módulos). A tradução mora em `rotulos` do dicionário do núcleo; chave sem tradução
 * cai num texto humanizado (nunca mostra snake_case nem camelCase).
 */

const t = (chave: string, opcoes?: Record<string, unknown>) => i18n.t(chave, opcoes);
const tem = (chave: string) => i18n.exists(chave);

function palavras(chave: string) {
  return chave
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[.\-\s]+/g, '_')
    .toLowerCase()
    .split('_')
    .filter(Boolean);
}

type Idioma = 'pt' | 'en' | 'es';
const idiomaAtual = (): Idioma => {
  const l = (i18n.language ?? 'pt').slice(0, 2);
  return l === 'en' || l === 'es' ? l : 'pt';
};

/** Palavras de estado e de tipo que o dicionário do núcleo ainda não cobre (pt|en|es). */
const EXTRAS = `
em|em|in|en
aberto|aberto|open|abierto
andamento|andamento|progress|curso
vencer|vencer|due|vencer
atrasado|atrasado|overdue|atrasado
vista|vista|sight|vista
pagar|pagar|payable|pagar
receber|receber|receivable|cobrar
nota|nota|note|nota
credito|crédito|credit|crédito
follow|follow|follow|follow
up|up|up|up
`;
const FRASES = `
a_vista|À vista|Upfront|Al contado
a_vencer|A vencer|Upcoming|Por vencer
em_aberto|Em aberto|Open|Abierto
em_andamento|Em andamento|In progress|En curso
recebido_parcial|Recebido parcial|Partially received|Recibido parcial
atrasado_parcial|Atrasado parcial|Partially overdue|Atrasado parcial
conta_pagar|Conta a pagar|Account payable|Cuenta por pagar
conta_receber|Conta a receber|Account receivable|Cuenta por cobrar
nota_credito|Nota de crédito|Credit note|Nota de crédito
follow_up|Follow-up|Follow-up|Seguimiento
sem_validade|Sem validade|No expiry|Sin vencimiento
nao_informado|Não informado|Not informed|No informado
`;
function linhas(texto: string) {
  const m = new Map<string, string[]>();
  for (const l of texto.trim().split('\n')) {
    const p = l.split('|');
    m.set(p[0]!, p.slice(1));
  }
  return m;
}
const EXTRAS_MAPA = linhas(EXTRAS);
const FRASES_MAPA = linhas(FRASES);
const IDX_IDIOMA = { pt: 0, en: 1, es: 2 };
const frase = (chave: string) => FRASES_MAPA.get(chave)?.[IDX_IDIOMA[idiomaAtual()]];
const extra = (p: string) => EXTRAS_MAPA.get(p)?.[IDX_IDIOMA[idiomaAtual()]];

const sabePalavra = (p: string) => tem(`rotulos.palavras.${p}`) || extra(p) !== undefined;
const traduzirPalavra = (p: string) => (tem(`rotulos.palavras.${p}`) ? t(`rotulos.palavras.${p}`) : (extra(p) ?? p));

/** Junta palavras traduzidas: primeira com inicial maiúscula, demais em minúscula (siglas ficam como estão). */
function juntar(ps: string[]) {
  return ps
    .map((p, i) => {
      const tr = traduzirPalavra(p);
      if (tr === tr.toUpperCase() && tr.length > 1) return tr;
      return i === 0 ? tr.charAt(0).toUpperCase() + tr.slice(1) : tr.charAt(0).toLowerCase() + tr.slice(1);
    })
    .join(' ');
}

/**
 * Fallback humanizado: sem sufixos técnicos (`_id`, `_centavos`), sem separadores.
 * Em en/es, texto que o dicionário não cobre por inteiro volta como veio: nunca mistura idiomas palavra por palavra.
 */
export function humanizar(chave: string) {
  const f = frase(palavras(chave).join('_'));
  if (f) return f;
  let ps = palavras(chave);
  while (ps.length > 1 && ['id', 'ids', 'centavos'].includes(ps[ps.length - 1]!)) ps = ps.slice(0, -1);
  if (idiomaAtual() !== 'pt' && !ps.every(sabePalavra)) return chave;
  return juntar(ps);
}

/** `cliente.criado` vira "Cliente criado". Sem tradução completa fora do pt, o código volta como veio. */
export function rotuloEvento(evento: string) {
  return humanizar(evento);
}

/** `financeiro.lancamentos` (tela de uma visão salva) vira "Lançamentos (Financeiro)" pelo i18n do módulo; sem rótulo, o código. */
export function rotuloTela(tela: string) {
  const [modulo, ...resto] = tela.split('.');
  const aba = resto.join('.');
  if (!modulo || !aba || !tem(`${modulo}:abas.${aba}`)) return tela;
  return `${t(`${modulo}:abas.${aba}`)} (${rotuloModulo(modulo)})`;
}

/** Campo que guarda identificador técnico (chave, código, tela, evento, slug, id, prefixo): o valor aparece cru, em mono. */
export const ehIdentificador = (campo: string) => /^(chave|codigo|tela|evento|slug|id|prefixo)([_.]|$)|[_.](chave|codigo|tela|evento|slug|id|prefixo)$/i.test(campo.replace(/^extras\./, ''));

// Review r17 visual B1: um job por linha; `apps/api/tests/rotulos-jobs-r17.test.ts` confere que todo job registrado (módulos,
// importadores e núcleo) está aqui nos três idiomas.
const JOBS = `
agenda.google.sincronizar|Sincronização com o Google Agenda|Google Calendar sync|Sincronización con Google Calendar
agenda.google.sincronizar_pessoal|Sincronização do Google Agenda pessoal|Personal Google Calendar sync|Sincronización de Google Calendar personal
agenda.google.enviar|Envio do agendamento ao Google Agenda|Appointment sent to Google Calendar|Envío de la cita a Google Calendar
agenda.aviso_reuniao|Aviso do agendamento com Google Meet|Appointment notice with Google Meet|Aviso de la cita con Google Meet
agenda.gravacao_aviso|Gravação da reunião recebida|Meeting recording received|Grabación de la reunión recibida
integracoes.conta_azul.sincronizar|Leitura da Conta Azul|Conta Azul sync|Lectura de Conta Azul
agenda.recursos.importar|Importação de recursos da agenda|Calendar resources import|Importación de recursos de la agenda
assinaturas.contratos.importar|Importação de contratos|Contracts import|Importación de contratos
cadastros.condicoes_pagamento.importar|Importação de condições de pagamento|Payment terms import|Importación de condiciones de pago
cadastros.formas_pagamento.importar|Importação de formas de pagamento|Payment methods import|Importación de formas de pago
cadastros.importar_csv|Importação de CSV de cadastros|Registry CSV import|Importación de CSV de registros
cadastros.landing.processar|Processamento de contato da landing page|Landing page contact processing|Procesamiento de contacto de la landing page
cadastros.parceiros.importar|Importação de parceiros|Partners import|Importación de socios
cadastros.parceiros_crm.importar|Importação de leads do CRM|CRM leads import|Importación de leads del CRM
cadastros.projetos.importar|Importação de projetos|Projects import|Importación de proyectos
cadastros.reconciliar_mescla|Reconciliação de mesclagem de cadastros|Record merge reconciliation|Conciliación de fusión de registros
cadastros.servicos.importar|Importação de serviços|Services import|Importación de servicios
cadastros.servicos_contratados.importar|Importação de serviços contratados|Contracted services import|Importación de servicios contratados
cadastros.setores.importar|Importação de setores|Departments import|Importación de sectores
cadastros.tags.importar|Importação de tags|Tags import|Importación de etiquetas
contas.asaas.processar|Processamento de aviso do Asaas|Asaas notice processing|Procesamiento de aviso de Asaas
contas.cancelar_cobranca|Cancelamento de cobrança|Charge cancellation|Cancelación de cobro
contas.estornar_cobranca|Estorno de cobrança|Charge refund|Reembolso de cobro
contas.gerar_cobranca|Geração de cobrança|Charge creation|Generación de cobro
contas.sicredi.gerar_cobranca|Geração de cobrança Pix no Sicredi|Sicredi Pix charge creation|Generación de cobro Pix en Sicredi
contas.sicredi.retomar_cobranca|Retomada de cobrança Pix no Sicredi|Sicredi Pix charge resumption|Reanudación de cobro Pix en Sicredi
contas.sicredi.processar|Processamento de aviso Pix do Sicredi|Sicredi Pix notice processing|Procesamiento de aviso Pix de Sicredi
contas.importar_documentos|Importação de documentos financeiros|Financial documents import|Importación de documentos financieros
contas.titulos_pagar.importar|Importação de títulos a pagar|Payables import|Importación de cuentas por pagar
contas.titulos_receber.importar|Importação de títulos a receber|Receivables import|Importación de cuentas por cobrar
conversas.email.sincronizar_caixa|Sincronização da caixa de e-mail|Email inbox sync|Sincronización del buzón de correo
conversas.enviar|Envio de mensagem de conversa|Conversation message delivery|Envío de mensaje de conversación
conversas.midia.baixar|Download de mídia de conversa|Conversation media download|Descarga de multimedia de conversación
conversas.templates.sincronizar|Sincronização de modelos de mensagem|Message templates sync|Sincronización de plantillas de mensaje
conversas.templates.enviar_aprovacao|Envio de modelo de mensagem para aprovação|Message template submission for review|Envío de plantilla de mensaje a revisión
conversas.templates.enviar_aprovacao|Envio de modelo de mensagem para aprovação|Message template submission for review|Envío de plantilla de mensaje a revisión
conversas.webhook.processar|Processamento de webhook de conversa|Conversation webhook processing|Procesamiento de webhook de conversación
conversas.comentario.responder|Resposta pública a comentário do Instagram|Public reply to an Instagram comment|Respuesta pública a comentario de Instagram
automacoes.executar|Execução de automação de conversa|Conversation automation run|Ejecución de automatización de conversación
automacoes.webhook|Webhook de automação de conversa|Conversation automation webhook|Webhook de automatización de conversación
automacoes.broadcast.despachar|Rodada de envio do Broadcast|Broadcast sending round|Ronda de envío de la difusión
automacoes.app_externo|Ação em app externo (Maton)|External app action (Maton)|Acción en app externa (Maton)
automacoes.maton_evento|Evento de app externo (Maton)|External app event (Maton)|Evento de app externa (Maton)
conversas.whatsapp.encerrar_gateway|Encerramento da sessão no gateway do WhatsApp não oficial|Unofficial WhatsApp gateway session shutdown|Cierre de la sesión en el gateway de WhatsApp no oficial
conversas.whatsapp.reconciliar|Reconciliação das sessões do WhatsApp não oficial|Unofficial WhatsApp sessions reconciliation|Reconciliación de las sesiones de WhatsApp no oficial
estoque.produtos.importar|Importação de produtos|Products import|Importación de productos
financeiro.categorias.importar|Importação de categorias|Categories import|Importación de categorías
financeiro.centros_custo.importar|Importação de centros de custo|Cost centers import|Importación de centros de costo
financeiro.contas.importar|Importação de contas financeiras|Financial accounts import|Importación de cuentas financieras
financeiro.exportar_contabil|Exportação contábil|Accounting export|Exportación contable
financeiro.importar_extrato|Importação de extrato bancário|Bank statement import|Importación de extracto bancario
documentos.avisar_signatarios|Aviso aos signatários|Notice to signers|Aviso a los firmantes
documentos.provedor.enviar|Envio ao provedor de assinatura|Sending to the signature provider|Envío al proveedor de firma
documentos.provedor.sincronizar|Consulta ao provedor de assinatura|Signature provider check|Consulta al proveedor de firma
documentos.provedor.cancelar|Cancelamento no provedor de assinatura|Cancellation at the signature provider|Cancelación en el proveedor de firma
documentos.provedor.webhook|Processamento de aviso do provedor de assinatura|Signature provider notice processing|Procesamiento de aviso del proveedor de firma
folha.fechar|Fechamento da folha|Payroll close|Cierre de nómina
integracoes.indices_bcb_agora|Atualização dos índices do Banco Central|Central Bank indices update|Actualización de los índices del Banco Central
nucleo.email.enviar|Envio de e-mail|Email delivery|Envío de correo
nucleo.evento.assinante|Entrega de evento a assinante|Event delivery to subscriber|Entrega de evento a suscriptor
nucleo.mcp.tool|Execução de tool MCP|MCP tool run|Ejecución de tool MCP
nucleo.push.enviar|Envio de notificação push|Push notification delivery|Envío de notificación push
nucleo.webhook.entregar|Entrega de webhook|Webhook delivery|Entrega de webhook
pessoas.documentos_vencendo|Alerta de documentos vencendo|Expiring documents alert|Alerta de documentos por vencer
pessoas.funcionarios.importar|Importação de funcionários|Employees import|Importación de empleados
ponto.importar_afd|Importação do AFD do relógio de ponto|Time clock AFD import|Importación del AFD del reloj de control
ponto.ler_relogio|Leitura do relógio de ponto pela API|Time clock API read|Lectura del reloj de control por la API
portal.avisar|Aviso de conteúdo novo ao cliente do portal|Client portal new content notice|Aviso de contenido nuevo al cliente del portal
portal.push|Push do app do cliente|Client app push|Push de la app del cliente
relatorios.exportar|Exportação de relatório|Report export|Exportación de informe
teste.isolamento|Teste de isolamento|Isolation test|Prueba de aislamiento
`;
const JOBS_MAPA = linhas(JOBS);

/** Tipo de job do worker (`folha.fechar`) como nome de tela no idioma do app; tipo novo cai no humanizado, nunca o nome técnico cru em pt. */
export function rotuloJob(tipo: string) {
  return JOBS_MAPA.get(tipo)?.[IDX_IDIOMA[idiomaAtual()]] ?? humanizar(tipo);
}

export function rotuloCampo(chave: string) {
  const c = chave.replace(/^extras\./, '');
  if (tem(`rotulos.campos.${c}`)) return t(`rotulos.campos.${c}`);
  return humanizar(c);
}

/**
 * Ação gravada pelo servidor (auditoria, atividade recente, histórico, permissão). Review r21 visual (M1): primeiro o rótulo
 * inteiro da ação (`rotulos.acoes`); sem ele, o humanizado palavra por palavra, que fora do pt devolve o código como veio
 * quando alguma palavra não tem tradução (antes juntava as palavras e misturava o pt no en: "Two-step verification ativar").
 */
export function rotuloAcao(acao: string) {
  if (tem(`rotulos.acoes.${acao}`)) return t(`rotulos.acoes.${acao}`);
  return humanizar(acao);
}

/**
 * Etapas de fábrica do pipeline (r7 visual B6): o módulo cria `ETAPAS_PADRAO` em pt (modules/pipeline/src/etapa/model/etapa.ts) e
 * a etapa não tem código próprio; o nome de fábrica, exato, faz as vezes do código. Fora do pt ele aparece traduzido; nome
 * personalizado (ou renomeado) aparece como está.
 */
const ETAPAS_FABRICA: Record<string, { en: string; es: string }> = {
  Novo: { en: 'New', es: 'Nuevo' },
  Contato: { en: 'Contact', es: 'Contacto' },
  Proposta: { en: 'Proposal', es: 'Propuesta' },
  Negociação: { en: 'Negotiation', es: 'Negociación' },
  Ganho: { en: 'Won', es: 'Ganado' },
  Perdido: { en: 'Lost', es: 'Perdido' },
};

export function rotuloEtapaPipeline(nome: string | null | undefined): string {
  if (!nome) return '';
  const idioma = idiomaAtual();
  const fabrica = Object.hasOwn(ETAPAS_FABRICA, nome) ? ETAPAS_FABRICA[nome] : undefined;
  return idioma === 'pt' || !fabrica ? nome : fabrica[idioma];
}

/** U13: o funil de fábrica do pipeline ("Modelo padrão"), gravado em pt como as etapas; o nome criado ou renomeado aparece como está. */
const FUNIS_FABRICA: Record<string, { en: string; es: string }> = {
  'Modelo padrão': { en: 'Default template', es: 'Modelo predeterminado' },
};

export function rotuloFunilPipeline(nome: string | null | undefined): string {
  if (!nome) return '';
  const idioma = idiomaAtual();
  const fabrica = Object.hasOwn(FUNIS_FABRICA, nome) ? FUNIS_FABRICA[nome] : undefined;
  return idioma === 'pt' || !fabrica ? nome : fabrica[idioma];
}

/**
 * Motivos de perda da semeadura do pipeline (`modules/pipeline/src/seeds.ts`), gravados em pt e sem código: como a etapa, o nome
 * exato faz as vezes do código (r10 visual M1). Motivo criado ou renomeado pela empresa aparece como está.
 */
const MOTIVOS_FABRICA: Record<string, { en: string; es: string }> = {
  'Preço acima do orçamento': { en: 'Price above budget', es: 'Precio por encima del presupuesto' },
  'Escolheu o concorrente': { en: 'Chose a competitor', es: 'Eligió a la competencia' },
  'Sem retorno do cliente': { en: 'No reply from the customer', es: 'Sin respuesta del cliente' },
  'Projeto adiado': { en: 'Project postponed', es: 'Proyecto aplazado' },
  'Fora do perfil': { en: 'Not a fit', es: 'Fuera del perfil' },
};

export function rotuloMotivoPerda(nome: string | null | undefined): string {
  if (!nome) return '';
  const idioma = idiomaAtual();
  const fabrica = Object.hasOwn(MOTIVOS_FABRICA, nome) ? MOTIVOS_FABRICA[nome] : undefined;
  return idioma === 'pt' || !fabrica ? nome : fabrica[idioma];
}

/**
 * Review r16 visual B6: categorias de papel que a migration da W7 (`financeiro/migrations/0002`) e a semeadura criam em pt,
 * sem código de tradução: o nome exato faz as vezes do código, como a etapa e o motivo de perda. Categoria criada ou
 * renomeada pela empresa aparece como está.
 */
const CATEGORIAS_FABRICA: Record<string, { en: string; es: string }> = {
  'Juros e multas recebidos': { en: 'Interest and late fees received', es: 'Intereses y multas cobrados' },
  'Descontos concedidos': { en: 'Discounts given', es: 'Descuentos concedidos' },
  'Juros e multas pagos': { en: 'Interest and late fees paid', es: 'Intereses y multas pagados' },
  'Descontos obtidos': { en: 'Discounts received', es: 'Descuentos obtenidos' },
  'Perdas com créditos': { en: 'Bad debt losses', es: 'Pérdidas por créditos' },
  'Tarifas bancárias': { en: 'Bank fees', es: 'Comisiones bancarias' },
};

export function rotuloCategoria(nome: string | null | undefined): string {
  if (!nome) return '';
  const idioma = idiomaAtual();
  const fabrica = Object.hasOwn(CATEGORIAS_FABRICA, nome) ? CATEGORIAS_FABRICA[nome] : undefined;
  return idioma === 'pt' || !fabrica ? nome : fabrica[idioma];
}

/** Código de evento ou de origem gravado pelo servidor (`funcionario.admitido`, `contas.recorrencia`). */
const CODIGO_PONTUADO = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;

export function rotuloModulo(slug: string, nomeServidor?: string) {
  if (tem(`rotulos.modulos.${slug}`)) return t(`rotulos.modulos.${slug}`);
  return nomeServidor && !/^[a-z0-9_.-]+$/.test(nomeServidor) ? nomeServidor : humanizar(slug);
}

/** `agenda.agendamento` vira "Agendamento (Agenda)". */
export function rotuloEntidade(entidade: string) {
  const [modulo, ...resto] = entidade.split('.');
  if (!resto.length) return humanizar(entidade);
  // Fase 2 (W14): nome composto irregular ("centro_custo" é "Cost center", não "Center cost") vem do mapa de campos.
  const chave = resto.join('_');
  const nome = tem(`rotulos.campos.${chave}`) ? t(`rotulos.campos.${chave}`) : juntar(palavras(chave));
  return `${nome} (${rotuloModulo(modulo!)})`;
}

/** Origem `{ modulo, entidade }` de um registro: "Recorrência (Contas)"; semeadura e importação viram texto próprio. */
export function rotuloOrigem(o: { modulo: string; entidade: string } | null | undefined) {
  if (!o) return '';
  if (['seed', 'demo', 'manual'].includes(o.entidade)) return `${traduzirPalavra(o.entidade === 'seed' ? 'demo' : o.entidade)} (${rotuloModulo(o.modulo)})`;
  return rotuloEntidade(`${o.modulo}.${o.entidade}`);
}

/**
 * O campo (pelo nome) some inteiro no modo discreto? Nascimento, partes do endereço (o número da casa também), agência e
 * dinheiro fora de `centavos` (review r14: a regra única `sensivelPorNome`, termo em qualquer posição da chave).
 */
export const campoOcultoNoDiscreto = (chave: string) => {
  const tipo = sensivelPorNome(chave);
  return ehOcultoPorNome(tipo) && !(tipo === 'dinheiro' && /centavos$/i.test(chave));
};

const ISO_DATA = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATAHORA = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Valor legível por tipo: sim/não, dinheiro, datas, CPF/CNPJ com máscara, enums traduzidos, lista e objeto por extenso. */
/** Os nomes dos status de proposta de fábrica (gravados em pt) pelo código do estado base. */
const STATUS_PROPOSTA_FABRICA: Record<string, string> = { Criada: 'criada', Enviada: 'enviada', Aceita: 'aceita', Recusada: 'recusada' };

const TIPOS_MEIO = ['comercial', 'celular', 'residencial', 'fax', 'outro', 'whatsapp', 'pessoal'];
/** Tipo de telefone ou e-mail do contato no idioma do app; o que não é conhecido passa como veio. */
function rotuloTipoMeio(s: string): string {
  if (s === 'pessoal') return { pt: 'Pessoal', en: 'Personal', es: 'Personal' }[idiomaAtual()];
  return tem(`cadastros:crm.aj.tiposTelefone.${s}`) ? t(`cadastros:crm.aj.tiposTelefone.${s}`) : s;
}
/** Telefones e e-mails adicionais chegam do servidor como "número (tipo), ..."; o tipo entre parênteses sai no idioma do app. */
const tiposDosMeios = (texto: string) => texto.replace(/\(([a-z]+)\)/g, (m, tp: string) => (TIPOS_MEIO.includes(tp) ? `(${rotuloTipoMeio(tp)})` : m));

export function rotuloValor(chave: string, v: unknown, moeda?: string): string {
  const k = chave.replace(/^extras\./, '');
  if (v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) return t('rotulos.vazio');
  if (typeof v === 'boolean') return v ? t('comum.sim') : t('comum.nao');
  // Modo discreto (r9 visual B1): nascimento, partes do endereço (inclusive o número) e agência somem antes de virar data ou número.
  if (discretoAtivo() && typeof v !== 'object' && campoOcultoNoDiscreto(k)) return OCULTO;
  // Review r14: o tipo sensível pelo nome vem da regra única (`nucleo/sensivel-por-nome.ts`), a mesma do Editar e da ficha.
  const tipo = sensivelPorNome(k);
  if (typeof v === 'number') return /centavos$/i.test(k) ? dinheiro(v, moeda) : numero(v, Number.isInteger(v) ? 0 : 2);
  if (Array.isArray(v)) return v.map((x) => rotuloValor(k, x, moeda)).join(', ');
  if (typeof v === 'object') {
    // Modo discreto (r9 visual B1): objeto de endereço mostra só cidade e UF, como o formatador (o número da casa também some).
    if (discretoAtivo() && !Array.isArray(v) && (/(^|[_.])enderecos?$/i.test(k) || 'logradouro' in v || 'cep' in v)) return endereco(v as EnderecoTexto) || OCULTO;
    const partes = Object.entries(v as Record<string, unknown>).filter(([, x]) => x !== null && x !== undefined && x !== '');
    return partes.length ? partes.map(([ck, x]) => rotuloValor(ck, x, moeda)).join(', ') : t('rotulos.vazio');
  }
  const s = String(v);
  if (tipo === 'pix') return chavePix(s);
  if (tipo === 'conta' && discretoAtivo()) return numeroConta(s);
  if (/(^|[_.])tela$/i.test(k)) return rotuloTela(s);
  // r10 visual M1: rótulo de fábrica gravado em pt (etapa e motivo de perda do pipeline) aparece no idioma do app.
  if (/(^|[_.])etapa(_nome)?$/i.test(k)) return rotuloEtapaPipeline(s);
  if (/(^|[_.])funil(_nome)?$/i.test(k)) return rotuloFunilPipeline(s);
  if (/(^|[_.])motivo_perda(_nome)?$/i.test(k)) return rotuloMotivoPerda(s);
  // Rlote-9 (SPEC-B1): o papel de fábrica `dono` aparece como TI (crm6 item 9); a promoção antiga gravou o nome "Dono".
  if (/(^|[_.])papel$/i.test(k) && (s === 'dono' || s === 'Dono')) return { pt: 'TI', en: 'IT', es: 'TI' }[idiomaAtual()];
  // Lista de 02/11: o tipo do telefone do contato (comercial, whatsapp...) pelo rótulo do Cadastros, no idioma do app (o "Sales" do
  // dicionário geral não serve aqui).
  if (k === 'telefone_tipo') return rotuloTipoMeio(s);
  // Rodada rlote-9 (VIS-M1): o histórico da proposta grava o nome do status (crm6 item 18); os de fábrica nasceram em pt e saem
  // no idioma do app pelo código do estado; o personalizado fica com o nome que o cliente deu.
  if (/^proposta_\d+$/.test(k) && STATUS_PROPOSTA_FABRICA[s]) return rotuloValor(k, STATUS_PROPOSTA_FABRICA[s]);
  // Lote crm5 (E10): regime tributário (do Cadastros e o pretendido da negociação) pelo rótulo do catálogo, no idioma do app.
  if (/^regime_(tributario|pretendido)$/.test(k) && tem(`cadastros:valores.regime.${s}`)) return t(`cadastros:valores.regime.${s}`);
  // r10 visual B1: evento ou origem gravados como código (`origem: funcionario.admitido`) viram texto no idioma, nunca a chave crua.
  if (/(^|[_.])(evento|origem|gatilho|causa)$/i.test(k) && CODIGO_PONTUADO.test(s)) return rotuloEvento(s);
  // Review r13 visual B3: entidade gravada como código (`entidade: pessoas.funcionario`, no diff do campo personalizado) vira
  // "Funcionário (Pessoas)" no idioma, nunca a chave crua.
  if (/(^|[_.])entidade$/i.test(k) && CODIGO_PONTUADO.test(s)) return rotuloEntidade(s);
  if (ehIdentificador(k) && !UUID.test(s)) return s;
  if (ISO_DATA.test(s) && dataIsoValida(s)) return data(s);
  if (ISO_DATAHORA.test(s) && dataIsoValida(s.slice(0, 10)) && !Number.isNaN(new Date(s).getTime())) return dataHora(s);
  if (/^\d{4}-\d{2}$/.test(s) && mesIsoValido(s)) return mesAno(s);
  if (ehDocumentoPorNome(tipo) && (/^\d{11}(\d{3})?$/.test(s) || discretoAtivo())) return documentoFormatado(s);
  if (tipo === 'telefone') return /^telefones$/.test(k) ? tiposDosMeios(telefoneFormatado(s)) : telefoneFormatado(s);
  // Modo discreto: e-mail mascarado; do endereço ficam só a cidade e a UF.
  if (tipo === 'email') return /^email_adicionais$/.test(k) ? tiposDosMeios(email(s)) : email(s);
  if (UUID.test(s)) return s.slice(0, 8);
  // snake_case nunca aparece cru: estado conhecido é traduzido; o que não está no dicionário é humanizado ("em_aberto" vira "Em aberto").
  if (/^[a-z][a-z0-9]*(_[a-z0-9]+)+$/.test(s)) return humanizar(s);
  if (/^[a-z][a-z0-9]*$/.test(s) && palavras(s).every(sabePalavra)) return juntar(palavras(s));
  // Texto livre (observação, descrição): no modo discreto, valor, documento, e-mail, telefone e CEP dentro da frase somem (r10 B5).
  return censurarTexto(s);
}

/** Datas ISO (aaaa-mm-dd) e dd/mm/aaaa dentro de um texto viram o formato do idioma do app. */
export function localizarDatas(texto: string): string {
  return texto.replace(/\b(\d{4})-(\d{2})-(\d{2})\b|\b(\d{2})\/(\d{2})\/(\d{4})\b/g, (m0, a, m, d, d2, m2, a2) => {
    const iso = a ? `${a}-${m}-${d}` : `${a2}-${m2}-${d2}`;
    return dataIsoValida(iso) ? data(iso) : m0;
  });
}

/** Valor de campo identificador que aparece como veio (sem rótulo): a tela o mostra em mono. */
export const valorCru = (campo: string, v: unknown) => typeof v === 'string' && ehIdentificador(campo) && rotuloValor(campo, v) === v;

const norm = (p: string) =>
  p
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, '_');

/**
 * Texto curto composto no servidor em pt ("02/10/2026 09:00, marcado", "2026-10-02 · em aberto", "Pedido PC-1"):
 * cada parte conhecida (data, estado, palavra) vira o formato e o idioma do app; o resto passa como veio.
 */
export function rotuloTextoLivre(texto: string | null | undefined): string {
  if (!texto) return '';
  return texto
    .split(/( · |, )/)
    .map((parte) => {
      if (parte === ' · ' || parte === ', ') return parte;
      const br = /^(\d{2})\/(\d{2})\/(\d{4})(?: (\d{2}:\d{2}))?$/.exec(parte);
      if (br && dataIsoValida(`${br[3]}-${br[2]}-${br[1]}`)) return br[4] ? dataHora(`${br[3]}-${br[2]}-${br[1]}T${br[4]}:00`) : data(`${br[3]}-${br[2]}-${br[1]}`);
      if (br) return parte;
      if (ISO_DATA.test(parte)) return data(parte);
      const chave = norm(parte);
      if (/^[a-z_]+$/.test(chave) && tem(`rotulos.palavras.${chave}`)) return t(`rotulos.palavras.${chave}`).toLowerCase();
      const comNumero = /^([A-Za-zÀ-ú]+) (\S*\d\S*)$/.exec(parte);
      if (comNumero && tem(`rotulos.palavras.${norm(comNumero[1]!)}`)) return `${t(`rotulos.palavras.${norm(comNumero[1]!)}`)} ${comNumero[2]}`;
      return localizarDatas(parte);
    })
    .join('');
}
